import { gql } from "@apollo/client"

void gql`
  fragment ExtendedProductAdminListItem on ProductSettingsModel {
    id
    name
    slug
    sku
    isFulfillmentReady
    fulfillmentChecklist {
      nameAndSku
      stockLevel
      labelSizeAndDesign
      containerMockups
      denverWarehouseAssigned
      stripeProductCreated
      shipHeroProductStockSetup
    }
  }
`

void gql`
  fragment ProductPublisher on ProductPublisherModel {
    email
    fullName
  }
`

const PRODUCT_VARIANTS_PRODUCT = gql`
  fragment ProductVariantsProduct on ProductVariantsProductModel {
    id
    name
    sku
  }
`

export const PRODUCT_VARIANTS = gql`
  fragment ProductVariants on ProductVariants {
    id
    name
    kind
    products {
      ...ProductVariantsProduct
    }
  }
  ${PRODUCT_VARIANTS_PRODUCT}
`

export const SHIPPING_PACKAGE = gql`
  fragment ShippingPackage on ShippingPackageModel {
    id
    name
    width
    height
    length
    dimensionsUnit
    createdAt
    updatedAt
    isDisabled
    carriers {
      id
      readable
      logo
      slug
      warehouse {
        warehouseName
        city
      }
    }
    sortWeight
    bundleSize
    packageImage
    easypostPredefinedPackage
    warehouseNames
  }
`

export const PRODUCT_OOS_ADMIN_LIST_ITEM = gql`
  fragment ProductOOSAdminListItem on ProductSettingsModel {
    id
    name
    sku
    totalOrders
    totalOOSOrders
    totalOOSItems
    restockDate
  }
`

const CUSTOM_ON_HOLD_REASON = gql`
  fragment CustomOnHoldReason on CustomOnHoldReason {
    id
    reason
    resolved
    resolvedBy {
      firstName
      lastName
    }
    addedBy {
      firstName
      lastName
    }
    createdAt
    resolvedAt
  }
`

const USER_PRODUCT_ORDER_SUB_LINE_ITEM_ADMIN_ITEM = gql`
  fragment UserProductOrderSubLineAdminItem on UserProductOrderSubLineItem {
    id
    file
    isPrepared
    count
  }
`

const USER_PRODUCT_ORDER_LINE_ITEM_ADMIN_ITEM = gql`
  fragment UserProductOrderLineAdminItem on UserProductOrderLineModel {
    id
    userProductId
    userProduct {
      name
      defaultShippingPackageId
      isSingleSkuBundle
      singleSkuBundleQuantity
      productSettings {
        genericLabelFile
        labelMockupsFile
        labelMockupsFileV2
        restockDate
        labelSize {
          width
          height
          unit
        }
        defaultShippingPackageId
        defaultInternationalShippingPackageId
        stockLevel {
          warehouseId
          fulfillmentSku
        }
      }
    }
    count
    shipmentId
    shipment {
      createdBy {
        email
      }
    }
    reshipmentId
    isReplacement
    warehouse {
      id
      warehouseName
      defaultCarrierId
      defaultInternationalCarrierId
      defaultExpressCarrierId
      codeName
      countryCode
      isShipHeroManaged
    }
    shipbobOrderId
    printBatches {
      printBatchId
      countPrepared
      confirmed
    }
    preparedBy {
      firstName
      lastName
    }
    details {
      title
      nameSupliful
      priceSupliful
      sku
      weight
      weightUnit
      isPrepared
      isRefunded
      preparedAt
      labelVerified
      labelUrl
      productImageUrl
      bundle
      fbaUnitsPerBox
      fbaBoxWidth
      fbaBoxHeight
      fbaBoxLength
    }
    fulfillmentFees {
      count
      fulfillmentFee
      fulfillmentFeeType
    }
    subLine {
      ...UserProductOrderSubLineAdminItem
    }
  }
  ${USER_PRODUCT_ORDER_SUB_LINE_ITEM_ADMIN_ITEM}
`

const SHIPPING_PACKAGE_ADMIN_ITEM = gql`
  fragment ShippingPackageAdminItem on ShippingPackage {
    id
    name
  }
`

export const SHIPMENT_LIST_ITEM = gql`
  fragment ShipmentListItem on Shipment {
    id
    carrier {
      name
    }
    shipmentWeight
    userProductOrder {
      orderId
      kind
    }
    status
    trackingNumber
    trackingUrl
    createdAt
    createdById
    completedById
    warehouseName
  }
`

export const SHIPMENT_ORDER_ITEM = gql`
  fragment ShipmentOrderItem on Shipment {
    id
    status
    completed
    completedAt
    sent
    replacement
    carrierId
    carrier {
      name
      externalId
    }
    trackingNumber
    trackingUrl
    shippingPackage {
      ...ShippingPackageAdminItem
    }
    shippingLabelCreated
    shippingLabelUrl
    reshippingCost
    createdAt
    createdBy {
      email
    }
    userProductOrder {
      kind
    }
    userProductOrderLines {
      ...UserProductOrderLineAdminItem
    }
    reshippedOrderLines {
      ...UserProductOrderLineAdminItem
    }
    reshipmentId
  }
  ${SHIPPING_PACKAGE_ADMIN_ITEM}
  ${USER_PRODUCT_ORDER_LINE_ITEM_ADMIN_ITEM}
`

const USER_PRODUCT_ORDER_ADDRESS = gql`
  fragment UserProductOrderAddress on UserProductOrderAddressModel {
    name
    firstName
    lastName
    address1
    address2
    city
    zip
    stateCode
    countryCode
    company
    phone
  }
`

// TODO Remove lines once OrderSplitButton has been ported to GraphQL.
export const ORDER_ADMIN_LIST_ITEM = gql`
  fragment OrderAdminListItem on UserProductOrderModel {
    id
    orderId
    kind
    status
    onHold
    createdAt
    fulfillmentDeadline
    isPriorityLaneFulfillment
    lineItemsCount
    allProductLabelsVerified
    shippingAddress {
      countryCode
    }
    user {
      id
      username
      email
    }
    lines {
      id
      userProductId
      shipmentId
      count
      warehouse {
        id
        warehouseName
      }
      details {
        title
        isPrepared
        sku
      }
    }
    details {
      charged
      express
    }
    itemCountTotal
    itemCountByWarehouse {
      count
      warehouseId
    }
  }
`

export const ORDER_ADMIN_BROWSE_ITEM = gql`
  fragment OrderAdminBrowseItem on UserProductOrderModel {
    id
    orderId
    kind
    invoiceMode
    status
    isReplacement
    onHold
    onHoldReasons
    customOnHoldReasons {
      ...CustomOnHoldReason
    }
    createdAt
    updatedAt
    completedAt
    cancelledAt
    fulfillmentDeadline
    lineItemsCount
    allProductLabelsVerified
    sampleDiscountApplied
    user {
      id
      username
    }
    lockedById
    lockedBy {
      firstName
      lastName
    }
    store {
      shopUrl
    }
    stripePaymentId
    chargeProcessingFee
    details {
      shippingCost
      chargedAmount
      refunded
      refundAmount
      express
      charged
      email
    }
    warehouseShelfBin {
      id
    }
    warehouseShelfReturnBin {
      id
    }
    shopifyDetails {
      shopifyId
      shopifyOrderName
    }
    tiktokDetails {
      tiktokOrderId
      isTiktokShipping
    }
    lines {
      ...UserProductOrderLineAdminItem
    }
    billingAddress {
      ...UserProductOrderAddress
    }
    shippingAddress {
      ...UserProductOrderAddress
    }
    isPriorityLaneFulfillment
    postPaymentBucketId
    fbaDetails {
      id
      paymentStatus
      paymentKind
      postPaymentDueDateDays
      postPaymentDueDateDaysType
      shippingLabelUrl
      trackingNumber
    }
    bulkDetails {
      id
      paymentStatus
      paymentKind
      postPaymentDueDateDays
      postPaymentDueDateDaysType
    }
  }
  ${CUSTOM_ON_HOLD_REASON}
  ${USER_PRODUCT_ORDER_LINE_ITEM_ADMIN_ITEM}
  ${USER_PRODUCT_ORDER_ADDRESS}
`

void gql`
  fragment LabelSize on ProductLabelSize {
    id
    internalId
    name
    width
    height
    unit
  }
`

const USER_STORE_ADMIN_ITEM = gql`
  fragment UserStoreAdminItem on UserStoreModel {
    id
    userId
    platform
    name
    shopUrl
    email
    address1
    address2
    city
    zip
    country
    webhooksInitialised {
      success
      reason
    }
    fulfillmentService {
      id
    }
  }
`

const USER_CERTIFICATE_ADMIN_ITEM = gql`
  fragment UserCertificateAdminItem on ExemptaxCustomerCertificate {
    id
    certificateId
    exemptionReason
    state
    expirationDate
  }
`

export const USER_ADMIN_ITEM = gql`
  fragment UserAdminItem on UserModel {
    id
    fullName
    username
    email
    emailVerified
    failedOrderPaymentsCount
    becameActiveAt
    createdAt
    updatedAt
    stripeCustomerId
    stripePostPaymentCustomerId
    currentSubscription {
      status
      tier
    }
    certificates {
      ...UserCertificateAdminItem
    }
    stores {
      ...UserStoreAdminItem
    }
    isPriorityLaneFulfillment
    isBrandAcceleratorParticipant
    vip
    isDisabled
    isDeactivated
    deactivationScheduledAt
    deactivatedAt
    isPostPaymentEnabled
    postPaymentPeriodWeeks
    postPaymentDueDateDays
    postPaymentDueDateDaysType
    fbaPaymentKind
    fbaPostPaymentDueDateDays
    fbaPostPaymentDueDateDaysType
    bulkOrderPaymentKind
    bulkOrderPostPaymentDueDateDays
    bulkOrderPostPaymentDueDateDaysType
  }
  ${USER_CERTIFICATE_ADMIN_ITEM}
  ${USER_STORE_ADMIN_ITEM}
`

export const USER_PRODUCT_ADMIN_LIST_ITEM = gql`
  fragment UserProductAdminListItem on UserProductModel {
    id
    userId
    user {
      fullName
      email
    }
    name
    isPublished
    isPrelabeled
    verified
    createdAt
    suplifulProduct {
      sku
      name
    }
    isSingleSkuBundle
  }
`

export const PRODUCT_ADMIN_SETTINGS = gql`
  fragment ProductAdminSettings on ProductSettingsModel {
    id
    stripeProductId
    name
    slug
    sku
    isFulfillmentReady
    isInExtendedCatalog
    isExclusive
    shelf
    genericLabelFile
    labelMockupsFile
    labelMockupsFileV2
    canvaTemplate
    priceSupliful
    status
    restockDate
    holdOrdersWhenOutOfStock
    productLabellingTechnique
    stockLevel {
      id
      warehouseId
      warehouse {
        codeName
        warehouseName
        isShipHeroManaged
      }
      quantity
    }
    discontinued
    defaultShippingPackageId
    defaultInternationalShippingPackageId
    fulfillmentChecklist {
      nameAndSku
      stockLevel
      labelSizeAndDesign
      containerMockups
      denverWarehouseAssigned
      stripeProductCreated
      shipHeroProductStockSetup
    }
  }
`

export const PRODUCT_LABEL_SIZE = gql`
  fragment ProductLabelSize on ProductLabelSize {
    id
    internalId
    name
    width
    height
    unit
    labelsPerProduct
  }
`

export const WAREHOUSE_FRAGMENT = gql`
  fragment Warehouse on Warehouse {
    id
    warehouseName
    email
    phone
    defaultCarrierId
    defaultInternationalCarrierId
    defaultExpressCarrierId
    canShipInternationally
    canShipExpress
    timezone
    admins {
      id
      firstName
      lastName
      email
    }
  }
`

export const WAREHOUSE_LIST_ITEM_FRAGMENT = gql`
  fragment WarehouseListItem on Warehouse {
    id
    warehouseName
    codeName
    city
    email
    phone
    countryCode
    timezone
    carriers {
      id
      name
      manifestAvailable
    }
    defaultCarrierId
    defaultInternationalCarrierId
    defaultExpressCarrierId
    isDisabled
    isShipHeroManaged
    canShipInternationally
    canShipExpress
  }
`

export const SHIPMENT_BATCH_FRAGMENT = gql`
  fragment ShipmentBatch on ShipmentBatch {
    id
    carrier {
      readable
    }
    completedBy {
      firstName
      lastName
    }
    printFileUrl
    createdAt
  }
`

export const CARRIER_FRAGMENT = gql`
  fragment Carrier on Carrier {
    id
    externalId
    name
    readable
    slug
    isDisabled
    logo
    description
    warehouseId
    pickupAvailable
    manifestAvailable
    warehouse {
      warehouseName
    }
    defaultShippingPackageId
    defaultShippingPackage {
      name
    }
  }
`

export const ADMIN_FRAGMENT = gql`
  fragment Admin on Admin {
    id
    email
    firstName
    lastName
    warehouseId
    isDisabled
    permissions
    lastLogin
    updatedAt
    warehouse {
      codeName
      warehouseName
      timezone
      isShipHeroManaged
    }
  }
`

export const WAREHOUSE_SHELF_FRAGMENT = gql`
  fragment WarehouseShelf on WarehouseShelf {
    id
    name
    type
    bins {
      id
      number
      userProductOrderId
    }
    returnBins {
      id
      number
    }
    warehouseId
    warehouse {
      warehouseName
      codeName
    }
  }
`

export const PRINT_FILE_QUEUE_FRAGMENT = gql`
  fragment PrintFileQueue on PrintFileQueueResponse {
    id
    internalId
    printFilesBySku {
      sku
      name
      productForm {
        id
        name
        slug
      }
      total
      totalPriority
      labellingTechnique
      activeBatches
    }
  }
`

const PRINT_BATCH_LINE_ITEM_ORDER_FRAGMENT = gql`
  fragment PrintBatchLineItemOrder on UserProductOrder {
    id
    orderId
    onHold
    status
    lockedById
    lineItemsCount
    isReplacement
    itemCountByWarehouse {
      warehouseId
      count
    }
    kind
    lines {
      id
      count
      shipmentId
      warehouse {
        id
        codeName
        warehouseName
        defaultCarrierId
        countryCode
      }
      details {
        labelUrl
        productImageUrl
        isPrepared
        title
        isRefunded
      }
      userProduct {
        defaultShippingPackageId
        productSettings {
          defaultShippingPackageId
          defaultInternationalShippingPackageId
        }
      }
    }
    details {
      charged
      express
    }
    warehouseShelfBins {
      id
    }
    warehouseShelfReturnBin {
      id
    }
    shippingAddress {
      city
      countryCode
      stateCode
      name
      firstName
      lastName
      address1
      zip
    }
  }
`

const PRINT_BATCH_LINE_ITEM_FRAGMENT = gql`
  fragment PrintBatchLineItem on UserProductOrderLine {
    id
    count
    printBatches {
      lineId
      printBatchId
      countBatched
      countPrepared
      confirmed
      sequence
    }
    shipmentId
    shipment {
      ...ShipmentOrderItem
    }
    userProduct {
      productSettings {
        defaultShippingPackageId
        defaultInternationalShippingPackageId
        genericLabelFile
      }
    }
    warehouse {
      id
      codeName
      countryCode
    }
    preparedBy {
      id
      firstName
      lastName
    }
    details {
      labelUrl
      productImageUrl
      isPrepared
      preparedAt
      title
    }
    order {
      ...PrintBatchLineItemOrder
    }
    subLine {
      ...UserProductOrderSubLineAdminItem
    }
  }
  ${SHIPMENT_ORDER_ITEM}
  ${PRINT_BATCH_LINE_ITEM_ORDER_FRAGMENT}
  ${USER_PRODUCT_ORDER_SUB_LINE_ITEM_ADMIN_ITEM}
`

export const BATCHED_PRINT_FRAGMENT = gql`
  fragment BatchedPrint on BatchedPrintResponse {
    id
    isSingleItemOrder
    isTiktokOrder
    userId
    sku
    productName
    productImage
    createdAt
    shelf
    url
    lines {
      id
      countPrepared
      confirmed
      line {
        ...PrintBatchLineItem
      }
    }
  }
  ${PRINT_BATCH_LINE_ITEM_FRAGMENT}
`

export const USER_CREDITS_TRANSACTION_FRAGMENT = gql`
  fragment UserCreditsTransaction on UserCreditsTransaction {
    id
    amountCents
    createdAt
    createdById
  }
`

export const DIGITAL_SERVICE_PRODUCT_FRAGMENT = gql`
  fragment DigitalServiceProduct on DigitalServiceProduct {
    stripeProductId
    hubspotProductId
    isActive
    createdAt
    updatedAt
    createdBy {
      email
    }
    createdById
    refundPolicy
  }
`

export const ADMIN_DIGITAL_SERVICE_PRODUCT_LIST_ITEM_FRAGMENT = gql`
  fragment AdminDigitalServiceProductListItem on AdminDigitalServiceProductListItem {
    digitalServiceProduct {
      ...DigitalServiceProduct
    }
    stripeProduct {
      id
      name
      description
      images
    }
  }
  ${DIGITAL_SERVICE_PRODUCT_FRAGMENT}
`

export const CATALOG_BANNER = gql`
  fragment CatalogBanner on CatalogBanner {
    id
    isDisabled
    name
    slug
    description
    bannerImage
    bannerImageSmall
    bannerVideo
    bannerVideoUrl
    pageImage
    pageImageSmall
    bannerImageUrl
    bannerImageSmallUrl
    pageImageUrl
    pageImageSmallUrl
    weight
    url
    timerDeadline
    timerLabel
  }
`

export const CATALOG_BANNER_COLLECTION = gql`
  fragment CatalogBannerCollection on CatalogBannerCollection {
    id
    isDisabled
    name
    kind
    banners {
      ...CatalogBanner
    }
  }
  ${CATALOG_BANNER}
`

const PRODUCT_SETTINGS = gql`
  fragment ProductSettings on ProductSettingsModel {
    isInExtendedCatalog
    shelf
    holdOrdersWhenOutOfStock
    variants {
      kind
      products {
        id
        sku
        name
      }
    }
  }
`

export const PRODUCT_ADMIN_LIST_ITEM = gql`
  fragment ProductAdminListItem on ProductSettingsModel {
    id
    name
    slug
    sku
    discontinued
    isFulfillmentReady
    priceSupliful
    stockLevel {
      id
      warehouseId
      warehouse {
        codeName
        warehouseName
        isShipHeroManaged
      }
      quantity
      fulfillmentSku
    }
    restockDate
    fulfillmentChecklist {
      nameAndSku
      stockLevel
      labelSizeAndDesign
      containerMockups
      denverWarehouseAssigned
      stripeProductCreated
      shipHeroProductStockSetup
    }
    ...ProductSettings
  }
  ${PRODUCT_SETTINGS}
`
